import { buildingForEachEntityOfType } from "building-common/buildingForEachEntity";
import { buildingGetEntity } from "building-common/buildingGetEntity";
import { materialKeyMeasureName } from "building-generation/utils/materialKeyMeasureName";
import { traverseRoofMeasurements } from "building-geometry/metrics/roofs/traverseRoofMeasurements";
import type { Building } from "building-types/building/buildingTypes";
import { isBuildingGeomAnalyticTag } from "building-types/buildingGeomTag/buildingGeomTags";
import type { AnalyticAreaKeyMeasures, AnalyticLengthKeyMeasures } from "building-types/dataSequences/AnalyticEntity";
import type {
  RoofAnalyticAreaKAM,
  RoofAnalyticAreaKAMOptions,
  RoofAnalyticEntityKeyAttributes,
  RoofAnalyticLengthKAM,
  RoofAnalyticLengthKAMOptions,
  RoofAreaKAM,
  RoofAreaKAMOptions,
  RoofAreaKeyAttributes,
  RoofAreaKeyMeasures,
  RoofEaveKAM,
  RoofEaveKAMOptions,
  RoofFlashingKAM,
  RoofFlashingKAMOptions,
  RoofKAM,
  RoofKAMOptions,
  RoofKeyAttributes,
  RoofKeyMeasures,
  RoofRidgeVentKAM,
  RoofRidgeVentKAMOptions,
  RoofValleyKAM,
  RoofValleyKAMOptions,
} from "building-types/dataSequences/builtin/RoofKAM";
import type {
  RoofRidgeKAM,
  RoofRidgeKAMOptions,
  LinearRoofKeyAttributes,
  LinearRoofKeyMeasures,
} from "building-types/dataSequences/builtin/RoofRidgeKAM";
import type { BuildingEntityFilter } from "building-types/dataSequences/filterableBuildingEntityTypes";
import type { KeyAttributesAndMeasuresDisplayOptions } from "building-types/dataSequences/measures";
import type { BuildingRoofSystem } from "building-types/entities/buildingEntity";
import { isBuildingEntityHandle } from "building-types/entities/buildingEntityHandle";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { ExteriorMaterial } from "building-types/materials/exteriorMaterialTypes";
import { RoomClass, RoomEnclosure } from "building-types/rooms/roomTypesEnum";
import type { WindowUnit } from "building-types/windows/windowTypes";
import { pathStringFromMaterialSrcWithUserDefinedName } from "common/materials/pathStringFromMaterialSrc";
import type { FeetSquared, WorldFeet } from "common/types/nominalNumbersTypes";
import { formatSlope } from "common/utils/formatSlope";
import { feetSquaredFromInchesSquared, feetFromWorldInches } from "common/utils/nominalNumbers";
import { multiPolyLineArea } from "doc/elements/floorAreaTakeoffs/takeoffs";
import { docEntityHandleIsGeneratedEntity } from "doc/entity/docEntityHandleTools";
import { formatInchesFraction } from "doc/measurementUtils";
import {
  keyAttributeNew,
  keyMeasureNew,
  keyAttributeOptionsNew,
  keyMeasureOptionsNew,
} from "doc/utils/dataSequenceTools";
import { DIST_EPSILON, DIST_EPSILON_SQUARED } from "geom/core";
import { polygonUnion, PolygonRoundingMode } from "geom/polyclip/polyClip";
import { polyLine2Area } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { type PolyLine3, polyLine3Length, polyLine3AtZ } from "geom/polyLine3";
import {
  type ConfigurableTable,
  configurableTableNew,
  categoryColumnNew,
  numberColumnNew,
  SortBy,
  stringColumnNew,
  aggregatorColumnNew,
} from "lib/house/dataSequences/dataSequenceConfigurableTable";
import { extractRoofPlanPerimsAnnoInfo } from "lib/house/plans/roof";

function makeRoofKAM(item: RoofAreaKeyMeasure): RoofKAM {
  return {
    type: "ROOF",
    attributes: {
      type: keyAttributeNew(item.category),
      name: keyAttributeNew(item.name),
      material: keyAttributeNew(item.material ?? ""),
      entityName: keyAttributeNew(item.category + item.name + item.material),
    },
    measures: {
      area: keyMeasureNew(item.area, "SQ FT"),
      count: keyMeasureNew(1),
    },
    handles: [...item.handles],
  };
}

export const EMPTY_ROOF_KAM: RoofKAM = {
  type: "ROOF",
  attributes: {
    type: keyAttributeNew(""),
    name: keyAttributeNew(""),
    material: keyAttributeNew(""),
    entityName: keyAttributeNew(""),
  },
  measures: {
    area: keyMeasureNew(0, "SQ FT"),
    count: keyMeasureNew(0),
  },
  handles: [],
};

export function makeDefaultRoofKAMOptions(): RoofKAMOptions {
  return {
    attributes: {
      entityName: keyAttributeOptionsNew({ definesEntity: false }),
    },
    measures: {
      area: keyMeasureOptionsNew({ unit: "SQ FT" }),
    },
  };
}

export const defaultRoofKAMSort = (a: RoofKAM, b: RoofKAM) => {
  return b.measures.area.value >= a.measures.area.value ? 1 : -1;
};

export const ROOF_DISPLAY_OPTIONS: KeyAttributesAndMeasuresDisplayOptions<RoofKeyAttributes, RoofKeyMeasures> = {
  attributes: {
    entityName: {
      hidden: true,
    },
  },
};

// Attributes used in procure entityHash
export const DEFINES_ROOF: (keyof RoofKeyAttributes)[] = ["type", "name", "material"];

function makeRoofAreaKAM(item: RoofAreaKeyMeasure): RoofAreaKAM {
  return {
    type: "ROOF_AREA",
    attributes: {
      type: keyAttributeNew(item.category),
      name: keyAttributeNew(item.name),
      entityName: keyAttributeNew(item.category + item.name),
    },
    measures: {
      area: keyMeasureNew(item.area, "SQ FT"),
      count: keyMeasureNew(1),
    },
    handles: [...item.handles],
  };
}

export const EMPTY_ROOF_AREA_KAM: RoofAreaKAM = {
  type: "ROOF_AREA",
  attributes: {
    type: keyAttributeNew(""),
    name: keyAttributeNew(""),
    entityName: keyAttributeNew(""),
  },
  measures: {
    area: keyMeasureNew(0, "SQ FT"),
    count: keyMeasureNew(0),
  },
  handles: [],
};

export function makeDefaultRoofAreaKAMOptions(): RoofAreaKAMOptions {
  return {
    attributes: {
      entityName: keyAttributeOptionsNew({ definesEntity: false }),
    },
    measures: {
      area: keyMeasureOptionsNew({ unit: "SQ FT" }),
    },
  };
}

export const defaultRoofAreaKAMSort = (a: RoofAreaKAM, b: RoofAreaKAM) => {
  return b.measures.area.value >= a.measures.area.value ? 1 : -1;
};

export const ROOF_AREA_DISPLAY_OPTIONS: KeyAttributesAndMeasuresDisplayOptions<
  RoofAreaKeyAttributes,
  RoofAreaKeyMeasures
> = {
  attributes: {
    entityName: {
      hidden: true,
    },
  },
};

export const DEFINES_ROOF_AREA: (keyof RoofAreaKeyAttributes)[] = ["type", "name"];

function makeRoofAnalyticAreaKAM(item: RoofAnalyticAreaKeyMeasure): RoofAnalyticAreaKAM {
  return {
    type: "ROOF_ANALYTIC_AREA",
    attributes: {
      id: keyAttributeNew(item.id),
      name: keyAttributeNew(item.name),
      condition: keyAttributeNew(item.condition),
      material: keyAttributeNew(item.material),
      roofId: keyAttributeNew(item.roofId),
      roofName: keyAttributeNew(item.roofName),
      slope: keyAttributeNew(formatSlope(item.slope, 2, true)),
    },
    measures: {
      area: keyMeasureNew(item.area, "SQ FT"),
      areaInPlan: keyMeasureNew(item.areaInPlan, "SQ FT"),
      perimeter: keyMeasureNew(item.perimeter, "FEET"),
    },
    handles: [...item.handles],
  };
}

export const EMPTY_ROOF_ANALYTIC_AREA_KAM: RoofAnalyticAreaKAM = {
  type: "ROOF_ANALYTIC_AREA",
  attributes: {
    id: keyAttributeNew(""),
    name: keyAttributeNew(""),
    condition: keyAttributeNew(""),
    material: keyAttributeNew(""),
    roofId: keyAttributeNew(""),
    roofName: keyAttributeNew(""),
    slope: keyAttributeNew(""),
  },
  measures: {
    area: keyMeasureNew(0, "SQ FT"),
    areaInPlan: keyMeasureNew(0, "SQ FT"),
    perimeter: keyMeasureNew(0, "FEET"),
  },
  handles: [],
};

export function makeDefaultRoofAnalyticAreaKAMOptions(): RoofAnalyticAreaKAMOptions {
  return {
    attributes: {
      id: keyAttributeOptionsNew({ definesEntity: true }),
      material: keyAttributeOptionsNew({ definesEntity: false }),
      roofId: keyAttributeOptionsNew({ definesEntity: false }),
      roofName: keyAttributeOptionsNew({ definesEntity: false }),
    },
    measures: {
      area: keyMeasureOptionsNew({ unit: "SQ FT" }),
      perimeter: keyMeasureOptionsNew({ unit: "FEET" }),
    },
  };
}

export const ROOF_ANALYTIC_AREA_DISPLAY_OPTIONS: KeyAttributesAndMeasuresDisplayOptions<
  RoofAnalyticEntityKeyAttributes,
  AnalyticAreaKeyMeasures
> = {
  attributes: {
    id: {
      hidden: true,
    },
    roofId: {
      hidden: true,
    },
    roofName: {
      hidden: false,
    },
    material: {
      hidden: false,
    },
  },
};

export const DEFINES_ROOF_ANALYTIC_AREA: (keyof RoofAnalyticEntityKeyAttributes)[] = ["id"];

export const defaultRoofAnalyticAreaKAMSort = (a: RoofAnalyticAreaKAM, b: RoofAnalyticAreaKAM) => {
  return b.measures.area.value >= a.measures.area.value ? 1 : -1;
};

function makeRoofAnalyticLengthKAM(item: RoofAnalyticLengthKeyMeasure): RoofAnalyticLengthKAM {
  return {
    type: "ROOF_ANALYTIC_LENGTH",
    attributes: {
      id: keyAttributeNew(item.id),
      name: keyAttributeNew(item.name),
      condition: keyAttributeNew(item.condition),
      roofId: keyAttributeNew(item.roofId),
      roofName: keyAttributeNew(item.roofName),
      slope: keyAttributeNew(formatSlope(item.slope, 2, true)),
    },
    measures: {
      length: keyMeasureNew(item.length, "FEET"),
      lengthInPlan: keyMeasureNew(item.lengthInPlan, "FEET"),
    },
    handles: [...item.handles],
  };
}

export const EMPTY_ROOF_ANALYTIC_LENGTH_KAM: RoofAnalyticLengthKAM = {
  type: "ROOF_ANALYTIC_LENGTH",
  attributes: {
    id: keyAttributeNew(""),
    name: keyAttributeNew(""),
    condition: keyAttributeNew(""),
    material: keyAttributeNew(""),
    roofId: keyAttributeNew(""),
    roofName: keyAttributeNew(""),
    slope: keyAttributeNew(""),
  },
  measures: {
    length: keyMeasureNew(0, "FEET"),
    lengthInPlan: keyMeasureNew(0, "FEET"),
  },
  handles: [],
};

export function makeDefaultRoofAnalyticLengthKAMOptions(): RoofAnalyticLengthKAMOptions {
  return {
    attributes: {
      id: keyAttributeOptionsNew({ definesEntity: true }),
      roofId: keyAttributeOptionsNew({ definesEntity: false }),
      roofName: keyAttributeOptionsNew({ definesEntity: false }),
    },
    measures: {
      length: keyMeasureOptionsNew({ unit: "FEET" }),
    },
  };
}

export const ROOF_ANALYTIC_LENGTH_DISPLAY_OPTIONS: KeyAttributesAndMeasuresDisplayOptions<
  RoofAnalyticEntityKeyAttributes,
  AnalyticLengthKeyMeasures
> = {
  attributes: {
    id: {
      hidden: true,
    },
    roofId: {
      hidden: true,
    },
    roofName: {
      hidden: false,
    },
  },
};

export const DEFINES_ROOF_ANALYTIC_LENGTH: (keyof RoofAnalyticEntityKeyAttributes)[] = ["id"];

export const defaultRoofAnalyticLengthKAMSort = (a: RoofAnalyticLengthKAM, b: RoofAnalyticLengthKAM) => {
  return b.measures.length.value >= a.measures.length.value ? 1 : -1;
};

function makeRoofRidgeKAM(item: LinearRoofKeyMeasure): RoofRidgeKAM {
  return {
    type: "ROOF_RIDGE",
    attributes: {
      type: keyAttributeNew(item.category),
      name: keyAttributeNew(item.name),
      entityName: keyAttributeNew(item.category + item.name),
    },
    measures: {
      length: keyMeasureNew(item.length, "FEET", "INCHES"),
      count: keyMeasureNew(1),
    },
    handles: [],
  };
}

export const EMPTY_ROOF_RIDGE_KAM: RoofRidgeKAM = {
  type: "ROOF_RIDGE",
  attributes: {
    type: keyAttributeNew(""),
    name: keyAttributeNew(""),
    entityName: keyAttributeNew(""),
  },
  measures: {
    length: keyMeasureNew(0, "FEET", "INCHES"),
    count: keyMeasureNew(0),
  },
  handles: [],
};

export function makeDefaultRoofRidgeKAMOptions(): RoofRidgeKAMOptions {
  return {
    attributes: {
      entityName: keyAttributeOptionsNew({ definesEntity: false }),
    },
    measures: {
      length: keyMeasureOptionsNew({ unit: "FEET" }),
    },
  };
}

export const defaultRoofRidgeKAMSort = (a: RoofRidgeKAM, b: RoofRidgeKAM) => {
  return b.measures.length.value >= a.measures.length.value ? 1 : -1;
};

export const LINEAR_ROOF_DISPLAY_OPTIONS: KeyAttributesAndMeasuresDisplayOptions<
  LinearRoofKeyAttributes,
  LinearRoofKeyMeasures
> = {
  attributes: {
    entityName: {
      hidden: true,
    },
  },
};

// Attributes used in procure entityHash
export const DEFINES_LINEAR_ROOF: (keyof LinearRoofKeyAttributes)[] = ["type", "name"];

function makeRoofRidgeVentKAM(item: LinearRoofKeyMeasure): RoofRidgeVentKAM {
  return {
    type: "ROOF_RIDGE_VENT",
    attributes: {
      type: keyAttributeNew(item.category),
      name: keyAttributeNew(item.name),
      entityName: keyAttributeNew(item.category + item.name),
    },
    measures: {
      length: keyMeasureNew(item.length, "FEET", "INCHES"),
      count: keyMeasureNew(1),
    },
    handles: [],
  };
}

export const EMPTY_ROOF_RIDGE_VENT_KAM: RoofRidgeVentKAM = {
  type: "ROOF_RIDGE_VENT",
  attributes: {
    type: keyAttributeNew(""),
    name: keyAttributeNew(""),
    entityName: keyAttributeNew(""),
  },
  measures: {
    length: keyMeasureNew(0, "FEET", "INCHES"),
    count: keyMeasureNew(0),
  },
  handles: [],
};

export function makeDefaultRoofRidgeVentKAMOptions(): RoofRidgeVentKAMOptions {
  return {
    attributes: {
      entityName: keyAttributeOptionsNew({ definesEntity: false }),
    },
    measures: {
      length: keyMeasureOptionsNew({ unit: "FEET" }),
    },
  };
}

export const defaultRoofRidgeVentKAMSort = (a: RoofRidgeVentKAM, b: RoofRidgeVentKAM) => {
  return b.measures.length.value >= a.measures.length.value ? 1 : -1;
};

function makeRoofFlashingKAM(item: LinearRoofKeyMeasure): RoofFlashingKAM {
  return {
    type: "ROOF_FLASHING",
    attributes: {
      type: keyAttributeNew(item.category),
      name: keyAttributeNew(item.name),
      entityName: keyAttributeNew(item.category + item.name),
    },
    measures: {
      length: keyMeasureNew(item.length, "FEET", "INCHES"),
      count: keyMeasureNew(1),
    },
    handles: [],
  };
}

export const EMPTY_ROOF_FLASHING_KAM: RoofFlashingKAM = {
  type: "ROOF_FLASHING",
  attributes: {
    type: keyAttributeNew(""),
    name: keyAttributeNew(""),
    entityName: keyAttributeNew(""),
  },
  measures: {
    length: keyMeasureNew(0, "FEET", "INCHES"),
    count: keyMeasureNew(0),
  },
  handles: [],
};

export function makeDefaultRoofFlashingKAMOptions(): RoofFlashingKAMOptions {
  return {
    attributes: {
      entityName: keyAttributeOptionsNew({ definesEntity: false }),
    },
    measures: {
      length: keyMeasureOptionsNew({ unit: "FEET" }),
    },
  };
}

export const defaultRoofFlashingKAMSort = (a: RoofFlashingKAM, b: RoofFlashingKAM) => {
  return b.measures.length.value >= a.measures.length.value ? 1 : -1;
};

function makeRoofValleyKAM(item: LinearRoofKeyMeasure): RoofValleyKAM {
  return {
    type: "ROOF_VALLEY",
    attributes: {
      type: keyAttributeNew(item.category),
      name: keyAttributeNew(item.name),
      entityName: keyAttributeNew(item.category + item.name),
    },
    measures: {
      length: keyMeasureNew(item.length, "FEET", "INCHES"),
      count: keyMeasureNew(1),
    },
    handles: [],
  };
}

export const EMPTY_ROOF_VALLEY_KAM: RoofValleyKAM = {
  type: "ROOF_VALLEY",
  attributes: {
    type: keyAttributeNew(""),
    name: keyAttributeNew(""),
    entityName: keyAttributeNew(""),
  },
  measures: {
    length: keyMeasureNew(0, "FEET", "INCHES"),
    count: keyMeasureNew(0),
  },
  handles: [],
};

export function makeDefaultRoofValleyKAMOptions(): RoofValleyKAMOptions {
  return {
    attributes: {
      entityName: keyAttributeOptionsNew({ definesEntity: false }),
    },
    measures: {
      length: keyMeasureOptionsNew({ unit: "FEET" }),
    },
  };
}

export const defaultRoofValleyKAMSort = (a: RoofValleyKAM, b: RoofValleyKAM) => {
  return b.measures.length.value >= a.measures.length.value ? 1 : -1;
};

function makeRoofEaveKAM(item: LinearRoofKeyMeasure): RoofEaveKAM {
  return {
    type: "ROOF_EAVE",
    attributes: {
      type: keyAttributeNew(item.category),
      name: keyAttributeNew(item.name),
      entityName: keyAttributeNew(item.category + item.name),
    },
    measures: {
      length: keyMeasureNew(item.length, "FEET", "INCHES"),
      count: keyMeasureNew(1),
    },
    handles: [],
  };
}

export const EMPTY_ROOF_EAVE_KAM: RoofEaveKAM = {
  type: "ROOF_EAVE",
  attributes: {
    type: keyAttributeNew(""),
    name: keyAttributeNew(""),
    entityName: keyAttributeNew(""),
  },
  measures: {
    length: keyMeasureNew(0, "FEET", "INCHES"),
    count: keyMeasureNew(0),
  },
  handles: [],
};

export function makeDefaultRoofEaveKAMOptions(): RoofEaveKAMOptions {
  return {
    attributes: {
      entityName: keyAttributeOptionsNew({ definesEntity: false }),
    },
    measures: {
      length: keyMeasureOptionsNew({ unit: "FEET" }),
    },
  };
}

export const defaultRoofEaveKAMSort = (a: RoofEaveKAM, b: RoofEaveKAM) => {
  return b.measures.length.value >= a.measures.length.value ? 1 : -1;
};

/** @deprecated - use the Building Query API instead */
export function buildingRoofKAMs(
  building: Building,
  filter?: BuildingEntityFilter,
): {
  roofs: RoofKAM[];
  ridges: RoofRidgeKAM[];
  ridgeVents: RoofRidgeVentKAM[];
  valleys: RoofValleyKAM[];
  eaves: RoofEaveKAM[];
  wallFlashing: RoofFlashingKAM[];
  areas: RoofAreaKAM[];
  analyticAreas: RoofAnalyticAreaKAM[];
  analyticLengths: RoofAnalyticLengthKAM[];
} {
  const roofKeyMeasures = getRoofKeyMeasures(building, filter);
  const roofs: RoofKAM[] = [];
  for (const itemId in roofKeyMeasures.roofing) {
    roofs.push(makeRoofKAM(roofKeyMeasures.roofing[itemId]));
  }

  const areas: RoofAreaKAM[] = [];
  for (const itemId in roofKeyMeasures.area) {
    areas.push(makeRoofAreaKAM(roofKeyMeasures.area[itemId]));
  }

  const ridges: RoofRidgeKAM[] = [];
  for (const itemId in roofKeyMeasures.ridge) {
    ridges.push(makeRoofRidgeKAM(roofKeyMeasures.ridge[itemId]));
  }

  const ridgeVents: RoofRidgeVentKAM[] = [];
  for (const itemId in roofKeyMeasures.ridgeVent) {
    ridgeVents.push(makeRoofRidgeVentKAM(roofKeyMeasures.ridgeVent[itemId]));
  }

  const valleys: RoofValleyKAM[] = [];
  for (const itemId in roofKeyMeasures.valley) {
    valleys.push(makeRoofValleyKAM(roofKeyMeasures.valley[itemId]));
  }

  const eaves: RoofEaveKAM[] = [];
  for (const itemId in roofKeyMeasures.eave) {
    eaves.push(makeRoofEaveKAM(roofKeyMeasures.eave[itemId]));
  }

  const wallFlashing: RoofFlashingKAM[] = [];
  for (const itemId in roofKeyMeasures.wallFlashing) {
    wallFlashing.push(makeRoofFlashingKAM(roofKeyMeasures.wallFlashing[itemId]));
  }

  const analyticAreas: RoofAnalyticAreaKAM[] = [];
  for (const itemId in roofKeyMeasures.analyticAreas) {
    analyticAreas.push(makeRoofAnalyticAreaKAM(roofKeyMeasures.analyticAreas[itemId]));
  }

  const analyticLengths: RoofAnalyticLengthKAM[] = [];
  for (const itemId in roofKeyMeasures.analyticLengths) {
    analyticLengths.push(makeRoofAnalyticLengthKAM(roofKeyMeasures.analyticLengths[itemId]));
  }

  return { roofs, areas, ridges, ridgeVents, valleys, eaves, wallFlashing, analyticAreas, analyticLengths };
}

export type RoofSubEntity =
  | "ROOF"
  | "ROOF_AREA"
  | "ROOF_ANALYTIC_AREA"
  | "ROOF_ANALYTIC_LENGTH"
  | "ROOF_RIDGE"
  | "ROOF_RIDGE_VENT"
  | "ROOF_VALLEY"
  | "ROOF_EAVE"
  | "ROOF_FLASHING";

export enum RoofKeyMeasureType {
  ROOF = "Roof",
  RIDGE = "Ridge",
  VALLEY = "Valley",
  EAVE = "Eave",
  RIDGE_VENT = "Ridge Vent",
  FLASHING = "Flashing",
  AREA = "Area",
  ANALYTIC_AREA = "Analytic Area",
  VAULTED_INTERIOR = "Interior Vaulted Ceiling",
  VAULTED_EXTERIOR = "Exterior Vaulted Ceiling",
}
interface StandardRoofKeyMeasure {
  readonly category: RoofKeyMeasureType;
  readonly name: string;
  readonly order: number;
  count?: number;
  length?: number;
  area?: number;
  material?: string;
  windows?: WindowUnit[];
}

type LinearRoofKeyMeasure = {
  readonly category: RoofKeyMeasureType;
  readonly name: string;
  readonly order: number;
  readonly length: number;
};

type RoofAreaKeyMeasure = {
  readonly category: RoofKeyMeasureType;
  readonly name: string;
  readonly order: number;
  readonly area: number;
  readonly material?: string;
  readonly handles: DocEntityHandle[];
};

type RoofAnalyticAreaKeyMeasure = {
  readonly id: string;
  readonly name: string;
  readonly condition: string;
  readonly category: RoofKeyMeasureType;
  readonly material: string;
  readonly area: FeetSquared;
  readonly areaInPlan: FeetSquared;
  readonly slope: number;
  readonly perimeter: WorldFeet;
  readonly roofId: string;
  readonly roofName: string;
  readonly handles: DocEntityHandle[];
};

type RoofAnalyticLengthKeyMeasure = {
  readonly id: string;
  readonly name: string;
  readonly condition: string;
  readonly category: RoofKeyMeasureType;
  readonly length: WorldFeet;
  readonly slope: number;
  readonly lengthInPlan: WorldFeet;
  readonly roofId: string;
  readonly roofName: string;
  readonly handles: DocEntityHandle[];
};

type RoofProjectedPerimeterRails = {
  readonly outerRails: PolyLine3[];
  readonly innerRails: PolyLine3[];
  readonly totalArea: number;
  readonly overhangArea: number;
};

type VaultedCeilingPerimeterRails = {
  readonly rails: PolyLine3[];
  readonly totalArea: number;
  readonly category: RoofKeyMeasureType;
};

type StandardRoofKeyMeasureSet = { [key: string]: StandardRoofKeyMeasure };

type RoofKeyMeasureSet = {
  roofing: { [key: string]: RoofAreaKeyMeasure };
  ridge: { [key: string]: LinearRoofKeyMeasure };
  ridgeVent: { [key: string]: LinearRoofKeyMeasure };
  valley: { [key: string]: LinearRoofKeyMeasure };
  eave: { [key: string]: LinearRoofKeyMeasure };
  wallFlashing: { [key: string]: LinearRoofKeyMeasure };
  area: { [key: string]: RoofAreaKeyMeasure };
  analyticAreas: { [key: string]: RoofAnalyticAreaKeyMeasure };
  analyticLengths: { [key: string]: RoofAnalyticLengthKeyMeasure };
};

function roofMaterialKeyMeasureNew(
  name: string,
  area: number,
  material: string,
  handles: DocEntityHandle[],
): RoofAreaKeyMeasure {
  return {
    category: RoofKeyMeasureType.ROOF,
    order: 0,
    name,
    area,
    material,
    handles,
  };
}

function roofAreaKeyMeasureNew(
  name: string,
  area: number,
  handles: DocEntityHandle[],
  category: RoofKeyMeasureType = RoofKeyMeasureType.AREA,
): RoofAreaKeyMeasure {
  return {
    category: category,
    order: 1,
    name,
    area,
    handles,
  };
}

function roofRidgeKeyMeasureNew(name: string, length: number): LinearRoofKeyMeasure {
  return {
    category: RoofKeyMeasureType.RIDGE,
    order: 2,
    name,
    length,
  };
}

function roofValleyKeyMeasureNew(name: string, length: number): LinearRoofKeyMeasure {
  return {
    category: RoofKeyMeasureType.VALLEY,
    order: 3,
    name,
    length,
  };
}

function roofEaveKeyMeasureNew(name: string, length: number): LinearRoofKeyMeasure {
  return {
    category: RoofKeyMeasureType.EAVE,
    order: 4,
    name,
    length,
  };
}

function roofRidgeVentKeyMeasureNew(name: string, length: number): LinearRoofKeyMeasure {
  return {
    category: RoofKeyMeasureType.RIDGE_VENT,
    order: 5,
    name,
    length,
  };
}

function roofFlashingKeyMeasureNew(name: string, length: number): LinearRoofKeyMeasure {
  return {
    category: RoofKeyMeasureType.FLASHING,
    order: 6,
    name,
    length,
  };
}

function getKeyMeasureUniqueKey(keyMeasure: RoofAreaKeyMeasure | LinearRoofKeyMeasure): string {
  return keyMeasure.category + keyMeasure.name + ((keyMeasure as RoofAreaKeyMeasure).material ?? "");
}

function getKeyMeasureKey(keyMeasure: StandardRoofKeyMeasure): string {
  return keyMeasure.category + keyMeasure.name + keyMeasure.material;
}

function mergeLinearRoofKeyMeasure(
  destination: LinearRoofKeyMeasure,
  source: LinearRoofKeyMeasure,
): LinearRoofKeyMeasure {
  return {
    ...destination,
    length: destination.length + source.length,
  };
}

function mergeRoofAreaKeyMeasure(destination: RoofAreaKeyMeasure, source: RoofAreaKeyMeasure): RoofAreaKeyMeasure {
  return {
    ...destination,
    area: destination.area + source.area,
    handles: destination.handles.concat(source.handles),
  };
}

function addLinearRoofKeyMeasureToSet(
  dstSet: { [key: string]: LinearRoofKeyMeasure },
  keyMeasure: LinearRoofKeyMeasure,
) {
  if (!keyMeasure.length) {
    return;
  }
  const key = getKeyMeasureUniqueKey(keyMeasure);
  const dst = dstSet[key];

  if (dst) {
    dstSet[key] = mergeLinearRoofKeyMeasure(dst, keyMeasure);
  } else {
    dstSet[key] = keyMeasure;
  }
}

function addRoofAreaKeyMeasureToSet(dstSet: { [key: string]: RoofAreaKeyMeasure }, keyMeasure: RoofAreaKeyMeasure) {
  if (!keyMeasure.area) {
    return;
  }
  const key = getKeyMeasureUniqueKey(keyMeasure);
  const dst = dstSet[key];

  if (dst) {
    dstSet[key] = mergeRoofAreaKeyMeasure(dst, keyMeasure);
  } else {
    dstSet[key] = keyMeasure;
  }
}

function addRoofKeyMeasure(dst: StandardRoofKeyMeasure, src: StandardRoofKeyMeasure): void {
  if (!(dst.category === src.category && dst.name === src.name)) {
    console.assert(false);
  }

  if (dst.area !== undefined) {
    dst.area += src.area ?? 0;
  }

  if (dst.length !== undefined) {
    dst.length += src.length ?? 0;
  }

  if (dst.count !== undefined) {
    dst.count += src.count ?? 0;
  }
}

function addRoofKeyMeasureToSet(dstSet: StandardRoofKeyMeasureSet, keyMeasure: StandardRoofKeyMeasure): void {
  const key = getKeyMeasureKey(keyMeasure);
  const dst = dstSet[key];

  if (dst) {
    addRoofKeyMeasure(dst, keyMeasure);
  } else {
    dstSet[key] = keyMeasure;
  }
}

export function getRailsLength(rails: PolyLine3[]): number {
  let totalLength = 0;

  for (let i = 0, l = rails.length; i < l; i++) {
    totalLength += polyLine3Length(rails[i]);
  }

  return totalLength;
}

export function getRoofPerimeterRails(building: Building): RoofProjectedPerimeterRails {
  const { uncoveredPerims, roofLevelPerims } = extractRoofPlanPerimsAnnoInfo(building);
  const { groundLevel } = building;
  const { levelDatumsGenerated } = groundLevel ?? {};
  const { grade = 0 } = levelDatumsGenerated ?? {};

  // area under roof
  // combine all levels
  const combinedPerims: PolyLine2[] = [];
  Object.values(roofLevelPerims).forEach((perims) => {
    combinedPerims.push(...perims);
  });

  const combinedPolygon = polygonUnion({
    pls: combinedPerims.map((x) => [x]),
    roundingMode: PolygonRoundingMode.SNAP_ROUND,
  }).flat();
  const totalArea = multiPolyLineArea(combinedPolygon) / 144;

  // here we determine the overhang area
  // take full projected area (with overhang) and subtract the area without overhang
  const combinedUncovered: PolyLine2[] = [];
  Object.values(uncoveredPerims).forEach((uncoveredPerim) => {
    combinedUncovered.push(...uncoveredPerim);
  });
  // combine all levels
  const unionedUncovered = polygonUnion({
    pls: combinedUncovered.map((x) => [x]),
    roundingMode: PolygonRoundingMode.SNAP_ROUND,
  }).flat();
  const overhangArea = multiPolyLineArea(unionedUncovered) / 144;

  return {
    outerRails: combinedPolygon.map((r) => polyLine3AtZ(r, grade)),
    innerRails: unionedUncovered.map((r) => polyLine3AtZ(r, grade)),
    totalArea,
    overhangArea: totalArea - overhangArea,
  };
}

function getVaultedCeilingPerimeterRails(
  building: Building,
  category: RoofKeyMeasureType = RoofKeyMeasureType.VAULTED_INTERIOR,
): VaultedCeilingPerimeterRails {
  const {
    doc: { ceilings: ceilingDefs },
    groundLevel,
    rooms,
  } = building;
  const { floorElevation = 0, floorPlateThickness = 0 } = groundLevel ?? {};

  let totalArea = 0;
  const rails: PolyLine2[] = [];
  Object.values(ceilingDefs).forEach((ceiling) => {
    if (ceiling.type === "VAULTED") {
      const { parentId } = ceiling;
      const room = parentId ? rooms[parentId] : undefined;
      if (room) {
        const { attributes, outline } = room;
        const roomType =
          attributes.roomClass === RoomClass.EXTERIOR || attributes.roomEnclosure === RoomEnclosure.EXTERIOR
            ? RoofKeyMeasureType.VAULTED_EXTERIOR
            : RoofKeyMeasureType.VAULTED_INTERIOR;
        if (roomType !== category) {
          return;
        }

        const area = polyLine2Area(outline);
        totalArea += area;
        rails.push(polyLine3AtZ(outline, floorElevation - floorPlateThickness));
      }
    }
  });
  return { rails, totalArea: totalArea / 144, category };
}

interface TraversedRoofArea {
  groupName: string;
  area: number;
  exteriorMaterial: ExteriorMaterial;
  count: number;
  handles: DocEntityHandle[];
}

interface TraversedRoofEave {
  name: string;
  length: number;
}

interface TraversedRoofRidge {
  name: string;
  length: number;
  count?: number;
}

interface TraversedRoofRidgeVent {
  name: string;
  length: number;
}

interface TraversedRoofValley {
  name: string;
  length: number;
  count?: number;
}

interface TraversedRoofFlashing {
  name: string;
  length: number;
}

/** @deprecated - use the Building Query API instead */
function getRoofKeyMeasures(building: Building, filter?: BuildingEntityFilter): RoofKeyMeasureSet {
  const { roofSystems: roofs, walls, exteriorPalette } = building;
  const result: RoofKeyMeasureSet = {
    roofing: {},
    ridge: {},
    ridgeVent: {},
    valley: {},
    eave: {},
    wallFlashing: {},
    area: {},
    analyticAreas: {},
    analyticLengths: {},
  };

  let filteredRoofs = roofs;
  if (filter) {
    const mutableFilteredRoofs: Record<string, (typeof roofs)[string]> = {};
    for (const roofSystem of Object.values(roofs)) {
      if (filter(roofSystem)) {
        mutableFilteredRoofs[roofSystem.id] = roofSystem;
      }
    }
    filteredRoofs = mutableFilteredRoofs;
  }

  traverseRoofMeasurements({
    roofs: filteredRoofs,
    walls,
    onTraversedRoofArea: (roofArea: TraversedRoofArea) => {
      const { groupName, area, exteriorMaterial, handles } = roofArea;
      const roofMaterial = materialKeyMeasureName(exteriorPalette, exteriorMaterial);
      addRoofAreaKeyMeasureToSet(result.roofing, roofMaterialKeyMeasureNew(groupName, area, roofMaterial, handles));
      // Here we also add the area to the generic "surface area" category regardless of material
      addRoofAreaKeyMeasureToSet(result.area, roofAreaKeyMeasureNew("Surface Area", area, handles));
    },
    onTraversedRoofEave: (roofEave: TraversedRoofEave) => {
      const { name, length } = roofEave;
      addLinearRoofKeyMeasureToSet(result.eave, roofEaveKeyMeasureNew(name, length));
    },
    onTraversedRoofRidge: (roofRidge: TraversedRoofRidge) => {
      const { name, length } = roofRidge;
      addLinearRoofKeyMeasureToSet(result.ridge, roofRidgeKeyMeasureNew(name, length));
    },
    onTraversedRoofRidgeVent: (roofRidgeVent: TraversedRoofRidgeVent) => {
      const { name, length } = roofRidgeVent;
      addLinearRoofKeyMeasureToSet(result.ridgeVent, roofRidgeVentKeyMeasureNew(name, length));
    },
    onTraversedRoofValley: (roofValley: TraversedRoofValley) => {
      const { name, length } = roofValley;
      addLinearRoofKeyMeasureToSet(result.valley, roofValleyKeyMeasureNew(name, length));
    },
    onTraversedRoofFlashing: (roofFlashing: TraversedRoofFlashing) => {
      const { name, length } = roofFlashing;
      addLinearRoofKeyMeasureToSet(result.wallFlashing, roofFlashingKeyMeasureNew(name, length));
    },
  });

  buildingForEachEntityOfType(building, "ANALYTIC_ROOF", (entity) => {
    if (entity.geometryType !== "FACE") {
      return;
    }
    const parentRoofHandles = entity.parents
      .filter(docEntityHandleIsGeneratedEntity)
      .map((g) => g.entity)
      .filter(isBuildingEntityHandle)
      .filter((h) => h.entityType === "ROOF_SYSTEM");
    if (parentRoofHandles.length === 0) {
      return;
    }
    for (const parentRoofHandle of parentRoofHandles) {
      const parentRoof = buildingGetEntity(building, parentRoofHandle) as BuildingRoofSystem;
      if (filter && !filter(parentRoof)) {
        continue;
      }
      result.analyticAreas[entity.id] = {
        id: entity.id,
        name: entity.name,
        condition: entity.tags
          .filter(isBuildingGeomAnalyticTag)
          .map((tag) => tag.condition)
          .join(" / "),
        category: RoofKeyMeasureType.ANALYTIC_AREA,
        material: entity.materialSrc ? pathStringFromMaterialSrcWithUserDefinedName(entity.materialSrc) : "",
        area: feetSquaredFromInchesSquared(entity.area),
        areaInPlan: feetSquaredFromInchesSquared(entity.areaInPlan),
        slope: entity.slope,
        perimeter: feetFromWorldInches(entity.perimeter),
        roofId: parentRoofHandle.id,
        roofName: parentRoof?.name,
        handles: [entity.docEntityHandle],
      };
    }
  });
  buildingForEachEntityOfType(building, "ANALYTIC_ROOF", (entity) => {
    if (entity.geometryType !== "LINE_SEGMENT") {
      return;
    }
    const parentRoofHandles = entity.parents
      .filter(docEntityHandleIsGeneratedEntity)
      .map((g) => g.entity)
      .filter(isBuildingEntityHandle)
      .filter((h) => h.entityType === "ROOF_SYSTEM");
    if (parentRoofHandles.length === 0) {
      return;
    }
    for (const parentRoofHandle of parentRoofHandles) {
      const parentRoof = buildingGetEntity(building, parentRoofHandle) as BuildingRoofSystem;
      if (filter && !filter(parentRoof)) {
        continue;
      }
      result.analyticLengths[entity.id] = {
        id: entity.id,
        name: entity.name,
        condition: entity.tags
          .filter(isBuildingGeomAnalyticTag)
          .map((tag) => tag.condition)
          .join(" / "),
        category: RoofKeyMeasureType.ANALYTIC_AREA,
        length: feetFromWorldInches(entity.length),
        slope: entity.slope,
        lengthInPlan: feetFromWorldInches(entity.lengthInPlan),
        roofId: parentRoofHandle.id,
        roofName: parentRoof?.name,
        handles: [entity.docEntityHandle],
      };
    }
  });

  // handle building level areas
  // Only include these if we're not filtering, as they are building-wide totals
  if (!filter) {
    const { totalArea, overhangArea } = getRoofPerimeterRails(building);
    addRoofAreaKeyMeasureToSet(result.area, roofAreaKeyMeasureNew("Projected Roof", totalArea, []));
    addRoofAreaKeyMeasureToSet(result.area, roofAreaKeyMeasureNew("Overhang Area", overhangArea, []));
    // add vaulted ceiling area
    const { totalArea: vaultedArea } = getVaultedCeilingPerimeterRails(building);
    addRoofAreaKeyMeasureToSet(
      result.area,
      roofAreaKeyMeasureNew("Vaulted Ceiling", vaultedArea, [], RoofKeyMeasureType.VAULTED_INTERIOR),
    );
    const { totalArea: vaultedExtArea } = getVaultedCeilingPerimeterRails(
      building,
      RoofKeyMeasureType.VAULTED_EXTERIOR,
    );
    addRoofAreaKeyMeasureToSet(
      result.area,
      roofAreaKeyMeasureNew("Vaulted Ceiling", vaultedExtArea, [], RoofKeyMeasureType.VAULTED_EXTERIOR),
    );
  }

  return result;
}

function getRoofsKeyMeasures(building: Building): StandardRoofKeyMeasureSet {
  const { roofSystems: roofs, walls, exteriorPalette } = building;
  const totalRoofKeyMeasures: StandardRoofKeyMeasureSet = {};

  traverseRoofMeasurements({
    roofs,
    walls,
    onTraversedRoofArea: (roofArea: TraversedRoofArea) => {
      const { groupName, area, exteriorMaterial } = roofArea;
      const roofMaterial = materialKeyMeasureName(exteriorPalette, exteriorMaterial);
      addRoofKeyMeasureToSet(totalRoofKeyMeasures, roofMaterialKeyMeasureNew("", area, roofMaterial, []));
      addRoofKeyMeasureToSet(totalRoofKeyMeasures, roofMaterialKeyMeasureNew(groupName, area, roofMaterial, []));
    },
    onTraversedRoofEave: (roofEave: TraversedRoofEave) => {
      const { name, length } = roofEave;
      addRoofKeyMeasureToSet(totalRoofKeyMeasures, roofEaveKeyMeasureNew("", length));
      addRoofKeyMeasureToSet(totalRoofKeyMeasures, roofEaveKeyMeasureNew(name, length));
    },
    onTraversedRoofRidge: (roofRidge: TraversedRoofRidge) => {
      const { name, length } = roofRidge;
      addRoofKeyMeasureToSet(totalRoofKeyMeasures, roofRidgeKeyMeasureNew("", length));
      addRoofKeyMeasureToSet(totalRoofKeyMeasures, roofRidgeKeyMeasureNew(name, length));
    },
    onTraversedRoofRidgeVent: (roofRidgeVent: TraversedRoofRidgeVent) => {
      const { name, length } = roofRidgeVent;
      addRoofKeyMeasureToSet(totalRoofKeyMeasures, roofRidgeVentKeyMeasureNew(name, length));
    },
    onTraversedRoofValley: (roofValley: TraversedRoofValley) => {
      const { name, length } = roofValley;
      addRoofKeyMeasureToSet(totalRoofKeyMeasures, roofValleyKeyMeasureNew(name, length));
    },
    onTraversedRoofFlashing: (roofFlashing: TraversedRoofFlashing) => {
      const { name, length } = roofFlashing;
      addRoofKeyMeasureToSet(totalRoofKeyMeasures, roofFlashingKeyMeasureNew("", length));
      addRoofKeyMeasureToSet(totalRoofKeyMeasures, roofFlashingKeyMeasureNew(name, length));
    },
  });

  const { totalArea, overhangArea } = getRoofPerimeterRails(building);
  addRoofKeyMeasureToSet(totalRoofKeyMeasures, roofAreaKeyMeasureNew("Projected Roof", totalArea, []));
  addRoofKeyMeasureToSet(totalRoofKeyMeasures, roofAreaKeyMeasureNew("Overhang Area", overhangArea, []));

  const { totalArea: vaultedArea } = getVaultedCeilingPerimeterRails(building);
  addRoofKeyMeasureToSet(
    totalRoofKeyMeasures,
    roofAreaKeyMeasureNew("Vaulted Ceiling", vaultedArea, [], RoofKeyMeasureType.VAULTED_INTERIOR),
  );

  const { totalArea: vaultedExtArea } = getVaultedCeilingPerimeterRails(building, RoofKeyMeasureType.VAULTED_EXTERIOR);
  addRoofKeyMeasureToSet(
    totalRoofKeyMeasures,
    roofAreaKeyMeasureNew("Vaulted Ceiling", vaultedExtArea, [], RoofKeyMeasureType.VAULTED_EXTERIOR),
  );

  return totalRoofKeyMeasures;
}

function isNonEmptyCounter(roofKeyMeasure: StandardRoofKeyMeasure): StandardRoofKeyMeasure | null {
  const { count = 0, length = 0, area = 0 } = roofKeyMeasure;

  if (count === 0 && length < DIST_EPSILON && area < DIST_EPSILON_SQUARED) {
    return null;
  }

  return roofKeyMeasure;
}

export function makeRoofKeyMeasuresTable(building: Building): ConfigurableTable {
  const roofKeyMeasures = getRoofsKeyMeasures(building);

  const formatInchesValue = (value?: number, undefinedVal: string = ""): string => {
    return value !== undefined ? formatInchesFraction(Math.round(value)) : undefinedVal;
  };

  return configurableTableNew<StandardRoofKeyMeasure, StandardRoofKeyMeasure>({
    iterables: roofKeyMeasures,
    requirement: isNonEmptyCounter,
    columns: [
      categoryColumnNew("Roof"),
      numberColumnNew({
        name: "Priority",
        hidden: true,
        fieldFunc: (counter) => counter.order,
        sortBy: SortBy.Ascending,
        useInDiff: false,
      }),
      stringColumnNew({
        name: "Type",
        fieldFunc: (counter) => (counter.name.length === 0 ? counter.category : ""),
      }),
      stringColumnNew({
        name: "Name",
        fieldFunc: (counter) => counter.name,
      }),
      stringColumnNew({
        name: "Length",
        fieldFunc: (counter) => formatInchesValue(counter.length, "NA"),
      }),
      stringColumnNew({
        name: "Material",
        fieldFunc: (counter) => counter.material ?? "",
      }),
    ],
    aggregator: aggregatorColumnNew({
      name: "Area (SF)",
      aggregatorFunc: (counter) => (counter.area ? Math.round(counter.area) : 0),
    }),
  });
}
